import React from "react";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";

export interface UploadErrorDetails {
    errorCode?: string;
    message: string;
    metadata?: Record<string, any>;
}

interface UploadErrorModalProps {
    isOpen: boolean;
    onClose: () => void;
    error?: UploadErrorDetails;
}

/**
 * Get user-friendly error message and title based on error code
 */
function getErrorContent(error?: UploadErrorDetails): {
    title: string;
    message: string;
    suggestions?: string[];
} {
    if (!error) {
        return {
            title: "Upload Failed",
            message: "Something went wrong while processing your file. Please try again."
        };
    }

    const { errorCode, metadata } = error;

    switch (errorCode) {
        case "FILE_SIZE_EXCEEDED":
            return {
                title: "File Too Large",
                message: `Your video file is too large to upload. Maximum file size is ${metadata?.maxSizeMB || 100}MB.`,
                suggestions: [
                    "Compress your video using a video editing tool",
                    "Reduce the video resolution or quality",
                    "Trim the video to a shorter duration"
                ]
            };

        case "DURATION_EXCEEDED":
            return {
                title: "Video Too Long",
                message: `Your video is too long. Maximum duration is ${metadata?.maxDurationSeconds || 30} seconds.`,
                suggestions: [
                    "Trim your video to the most important sections",
                    "Split your video into smaller segments"
                ]
            };

        case "INVALID_FORMAT":
            return {
                title: "Unsupported Format",
                message: "This video format is not supported.",
                suggestions: [
                    "Convert your video to MP4 or WebM format",
                    "Try re-exporting from your video editor with standard settings"
                ]
            };

        case "PROCESSING_FAILED":
            return {
                title: "Processing Failed",
                message:
                    "We couldn't process your video. The file might be corrupted or use an unsupported codec.",
                suggestions: [
                    "Try re-exporting your video from the original source",
                    "Convert your video to a standard format (MP4 with H.264 codec)",
                    "If the problem persists, try a different video file"
                ]
            };

        case "DOWNLOAD_FAILED":
            return {
                title: "Upload Failed",
                message: "We couldn't access your video file. Please try uploading again.",
                suggestions: ["Check your internet connection", "Try uploading the file again"]
            };

        case "VALIDATION_ERROR":
            return {
                title: "Invalid Request",
                message: error.message || "The upload request was invalid. Please try again."
            };

        default:
            return {
                title: "Upload Failed",
                message:
                    error.message ||
                    "Something went wrong while processing your file. Please try again.",
                suggestions: [
                    "Check that your file is a valid video or image",
                    "Try uploading a different file",
                    "If the problem persists, contact support"
                ]
            };
    }
}

export const UploadErrorModal: React.FC<UploadErrorModalProps> = ({ isOpen, onClose, error }) => {
    const content = getErrorContent(error);

    return (
        <ModalWithIcon
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={onClose}
            type="error"
            size="md"
            icon="far fa-exclamation-triangle"
            title={content.title}
            submitButtonText="Ok"
            submitButtonProps={{ type: "secondary" }}
            hideCancel
        >
            <div className="flex flex-col gap-4 text-left">
                <p className="text-gray-700">{content.message}</p>

                {content.suggestions && content.suggestions.length > 0 && (
                    <div className="mt-2">
                        <p className="mb-2 font-medium text-gray-900">Suggestions:</p>
                        <ul className="list-inside list-disc space-y-1 text-gray-700">
                            {content.suggestions.map((suggestion, index) => (
                                <li key={index} className="text-sm">
                                    {suggestion}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </ModalWithIcon>
    );
};
